/**
 * Score Criteria Tab
 *
 * Manage score criteria definitions for levels 1-5.
 * Supports general criteria and task/competency-specific overrides.
 * Includes scorePercentage to configure how each score contributes to weighted calculations.
 */

import { useState, useMemo } from 'react';
import { useScoreCriteria, useUpdateScoreCriteria } from '../useSettings';
import type { ScoreCriteriaDTO, UpdateScoreCriteriaRequest } from '@functieloonhuis/shared';

interface EditingCriteria {
  scoreLevel: number;
  label: string;
  description: string;
  examples: string;
  minPercentage: number;
  maxPercentage: number;
  scorePercentage: number; // The percentage value this score contributes to weighted calculations
}

interface ValidationError {
  level?: number;
  message: string;
}

function validatePercentageRanges(criteria: EditingCriteria[]): ValidationError[] {
  const errors: ValidationError[] = [];
  const sorted = [...criteria].sort((a, b) => a.scoreLevel - b.scoreLevel);

  for (let i = 0; i < sorted.length; i++) {
    const current = sorted[i]!;

    // Check min <= max (both inclusive, so min can equal max for single-value range)
    if (current.minPercentage > current.maxPercentage) {
      errors.push({
        level: current.scoreLevel,
        message: `Score ${current.scoreLevel}: Vanaf (${current.minPercentage}%) moet kleiner of gelijk zijn aan t/m (${current.maxPercentage}%)`,
      });
    }

    // Check contiguity with previous level: current.min should be prev.max + 1
    if (i > 0) {
      const prev = sorted[i - 1]!;
      if (current.minPercentage !== prev.maxPercentage + 1) {
        errors.push({
          level: current.scoreLevel,
          message: `Score ${current.scoreLevel}: Vanaf (${current.minPercentage}%) sluit niet aan op Score ${prev.scoreLevel} t/m (${prev.maxPercentage}%)`,
        });
      }
    }
  }

  // Check first starts at 0
  if (sorted.length > 0 && sorted[0]!.minPercentage !== 0) {
    errors.push({
      level: sorted[0]!.scoreLevel,
      message: `Score 1: Moet beginnen bij 0%, niet ${sorted[0]!.minPercentage}%`,
    });
  }

  // Check last ends at 100
  if (sorted.length > 0 && sorted[sorted.length - 1]!.maxPercentage !== 100) {
    errors.push({
      level: sorted[sorted.length - 1]!.scoreLevel,
      message: `Score 5: Moet eindigen bij 100%, niet ${sorted[sorted.length - 1]!.maxPercentage}%`,
    });
  }

  return errors;
}

// Default scorePercentage values for each level
const DEFAULT_SCORE_PERCENTAGES: Record<number, number> = {
  1: 0,
  2: 25,
  3: 50,
  4: 75,
  5: 100,
};

const SCORE_LEVELS = [1, 2, 3, 4, 5];

const SCORE_COLORS: Record<number, string> = {
  1: 'bg-red-100 text-red-800 border-red-200',
  2: 'bg-orange-100 text-orange-800 border-orange-200',
  3: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  4: 'bg-lime-100 text-lime-800 border-lime-200',
  5: 'bg-green-100 text-green-800 border-green-200',
};

export function ScoreCriteriaTab() {
  const { data: criteria, isLoading, error } = useScoreCriteria('general');
  const updateCriteria = useUpdateScoreCriteria();

  const [isEditing, setIsEditing] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState<EditingCriteria[]>([]);

  const handleStartEdit = () => {
    // Initialize editing state with current criteria
    const initialCriteria: EditingCriteria[] = SCORE_LEVELS.map((level) => {
      const existing = criteria?.find((c) => c.scoreLevel === level);
      return {
        scoreLevel: level,
        label: existing?.label || '',
        description: existing?.description || '',
        examples: existing?.examples || '',
        minPercentage: existing?.minPercentage ?? 0,
        maxPercentage: existing?.maxPercentage ?? 100,
        scorePercentage: existing?.scorePercentage ?? DEFAULT_SCORE_PERCENTAGES[level] ?? 0,
      };
    });
    setEditingCriteria(initialCriteria);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingCriteria([]);
  };

  const handleSave = async () => {
    const data: UpdateScoreCriteriaRequest = {
      contextType: 'general',
      criteria: editingCriteria.map((c) => ({
        scoreLevel: c.scoreLevel,
        label: c.label,
        description: c.description,
        examples: c.examples || undefined,
        minPercentage: c.minPercentage,
        maxPercentage: c.maxPercentage,
        scorePercentage: c.scorePercentage,
      })),
    };

    await updateCriteria.mutateAsync(data);
    setIsEditing(false);
    setEditingCriteria([]);
  };

  const handleCriteriaChange = (
    level: number,
    field: 'label' | 'description' | 'examples' | 'minPercentage' | 'maxPercentage' | 'scorePercentage',
    value: string | number
  ) => {
    setEditingCriteria((prev) => {
      // Update the current field
      let newCriteria = prev.map((c) =>
        c.scoreLevel === level ? { ...c, [field]: value } : c
      );

      // Auto-sync adjacent boundaries to maintain contiguity
      // Max is inclusive (t/m), so next level's min = this max + 1
      if (field === 'maxPercentage' && typeof value === 'number') {
        // When changing max (t/m), update next level's min (vanaf) to max + 1
        newCriteria = newCriteria.map((c) =>
          c.scoreLevel === level + 1 ? { ...c, minPercentage: value + 1 } : c
        );
      } else if (field === 'minPercentage' && typeof value === 'number') {
        // When changing min (vanaf), update previous level's max (t/m) to min - 1
        newCriteria = newCriteria.map((c) =>
          c.scoreLevel === level - 1 ? { ...c, maxPercentage: value - 1 } : c
        );
      }

      return newCriteria;
    });
  };

  // Calculate validation errors for current editing state
  const validationErrors = useMemo(() => {
    if (!isEditing || editingCriteria.length === 0) return [];
    return validatePercentageRanges(editingCriteria);
  }, [isEditing, editingCriteria]);

  const hasValidationErrors = validationErrors.length > 0;

  const getCriteriaForLevel = (level: number): ScoreCriteriaDTO | undefined => {
    return criteria?.find((c) => c.scoreLevel === level);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">Fout bij het laden van score criteria.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Score Criteria</h2>
          <p className="text-sm text-gray-500">
            Definieer betekenis en verwachtingen voor elk scoreniveau (1-5)
          </p>
        </div>
        {!isEditing && (
          <button
            onClick={handleStartEdit}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Bewerken
          </button>
        )}
      </div>

      {/* Info Banner */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-700">
            <p className="mb-1">Deze criteria worden getoond als hulp tijdens het beoordelen. Ze helpen managers om consistente scores te geven.</p>
            <p className="mb-1 text-blue-600">De <strong>percentage grenzen</strong> bepalen welke score wordt toegekend op basis van het behaalde percentage (bijv. 70-84% = Score 4 "Goed").</p>
            <p className="text-blue-600">De <strong>scorewaarde %</strong> bepaalt hoeveel procent een individuele score bijdraagt aan de gewogen berekening (bijv. Score 3 = 50%).</p>
          </div>
        </div>
      </div>

      {/* Score Criteria Cards */}
      <div className="space-y-4">
        {SCORE_LEVELS.map((level) => {
          const criteriaItem = getCriteriaForLevel(level);
          const editingItem = editingCriteria.find((c) => c.scoreLevel === level);

          return (
            <div
              key={level}
              className={`border rounded-lg p-4 ${isEditing ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200'}`}
            >
              <div className="flex items-start gap-4">
                {/* Score Level Badge */}
                <div className="flex-shrink-0">
                  <span
                    className={`inline-flex items-center justify-center w-12 h-12 rounded-full text-xl font-bold border-2 ${SCORE_COLORS[level]}`}
                  >
                    {level}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <>
                      {/* Editing Mode */}
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Label *
                            </label>
                            <input
                              type="text"
                              value={editingItem?.label || ''}
                              onChange={(e) => handleCriteriaChange(level, 'label', e.target.value)}
                              placeholder="bijv. Onvoldoende, Goed, Uitstekend..."
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            />
                          </div>
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Vanaf %
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={editingItem?.minPercentage ?? 0}
                                onChange={(e) => handleCriteriaChange(level, 'minPercentage', parseInt(e.target.value) || 0)}
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                t/m %
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={editingItem?.maxPercentage ?? 100}
                                onChange={(e) => handleCriteriaChange(level, 'maxPercentage', parseInt(e.target.value) || 0)}
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Scorewaarde %
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={editingItem?.scorePercentage ?? DEFAULT_SCORE_PERCENTAGES[level] ?? 0}
                                onChange={(e) => handleCriteriaChange(level, 'scorePercentage', parseInt(e.target.value) || 0)}
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Beschrijving *
                          </label>
                          <textarea
                            value={editingItem?.description || ''}
                            onChange={(e) => handleCriteriaChange(level, 'description', e.target.value)}
                            placeholder="Beschrijf wanneer deze score van toepassing is..."
                            rows={2}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Voorbeelden (optioneel)
                          </label>
                          <textarea
                            value={editingItem?.examples || ''}
                            onChange={(e) => handleCriteriaChange(level, 'examples', e.target.value)}
                            placeholder="Concrete voorbeelden van gedrag of prestaties..."
                            rows={2}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* View Mode */}
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {criteriaItem?.label || `Score ${level}`}
                        </h3>
                        <div className="flex gap-2">
                          <span className="text-sm font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {criteriaItem?.minPercentage ?? 0}% t/m {criteriaItem?.maxPercentage ?? 100}%
                          </span>
                          <span className="text-sm font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded" title="Scorewaarde voor berekening">
                            = {criteriaItem?.scorePercentage ?? DEFAULT_SCORE_PERCENTAGES[level]}%
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {criteriaItem?.description || (
                          <span className="italic text-gray-400">Geen beschrijving ingesteld</span>
                        )}
                      </p>
                      {criteriaItem?.examples && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs font-medium text-gray-500 uppercase mb-1">Voorbeelden</p>
                          <p className="text-sm text-gray-500">{criteriaItem.examples}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Validation Errors (Edit Mode) */}
      {isEditing && hasValidationErrors && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-red-700">
              <p className="font-medium mb-1">Percentage grenzen kloppen niet:</p>
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error.message}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons (Edit Mode) */}
      {isEditing && (
        <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleCancelEdit}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Annuleren
          </button>
          <button
            onClick={handleSave}
            disabled={updateCriteria.isPending || hasValidationErrors}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateCriteria.isPending ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Opslaan...
              </>
            ) : (
              'Opslaan'
            )}
          </button>
        </div>
      )}

      {/* Preview Section */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="text-md font-medium text-gray-900 mb-4">Score Schaal Overzicht</h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between gap-2">
            {SCORE_LEVELS.map((level) => {
              const criteriaItem = getCriteriaForLevel(level);
              return (
                <div key={level} className="flex-1 text-center">
                  <div
                    className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-lg font-bold border-2 ${SCORE_COLORS[level]} mb-2`}
                  >
                    {level}
                  </div>
                  <p className="text-xs font-medium text-gray-600 truncate">
                    {criteriaItem?.label || '-'}
                  </p>
                  <p className="text-xs text-gray-400">
                    {criteriaItem?.minPercentage ?? 0}% t/m {criteriaItem?.maxPercentage ?? 100}%
                  </p>
                  <p className="text-xs text-blue-600 font-medium">
                    = {criteriaItem?.scorePercentage ?? DEFAULT_SCORE_PERCENTAGES[level]}%
                  </p>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex items-center justify-center">
            <div className="flex-1 h-2 bg-gradient-to-r from-red-400 via-yellow-400 to-green-400 rounded-full"></div>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span>0%</span>
            <span>100%</span>
          </div>
        </div>
      </div>

      {/* Score Calculation Example */}
      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">Berekeningsvoorbeeld:</p>
            <p>Als een taak (weging 60%) score <strong>4</strong> krijgt en een andere taak (weging 40%) score <strong>3</strong>:</p>
            <p className="mt-1">
              Percentage = (75% × 60%) + (50% × 40%) = 45% + 20% = <strong>65%</strong>
            </p>
            <p className="text-amber-600 mt-1">
              Dit percentage valt in de range 55-69% en resulteert in eindoordeel <strong>"Voldoende"</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
